import {
	Pagination,
	Box,
	Paper,
	Table,
	TableContainer,
	TableHead,
	TableRow,
	TableBody,
	CircularProgress,
	Collapse,
	IconButton,
	Chip,
	Typography,
	Dialog,
	DialogTitle,
	DialogContent,
	Theme,
} from '@mui/material'
import { ScaffoldFilters, ScaffoldWorkItem } from '../../types/Scaffold/Scaffold'
import { StyledTableCell } from '../core/StyledTableCell'
import React from 'react'
import { Dispatch, SetStateAction, useEffect, useState, useMemo } from 'react'
import { ScaffoldRequestForm } from '../../types/Scaffold/ScaffoldRequest'
import {
	createScaffoldWorkItem,
	getScaffoldOrders,
	getScaffoldUpdateRecord,
} from '../../services/scaffoldRequestService'
import {
	formatLocaleShortDate,
	formatLocaleShortDateTime,
	formatPercent,
} from '../../services/CommonServices/DataFormatters'
import { useProjectFeaturesContext } from '../project/contexts/ProjectFeaturesContext'
import { useLocation, useNavigate } from 'react-router-dom'
import RequestDetails from './RequestDetails'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import AssignToDialog from './AssignToDialog'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { getDecisionIcon, getDecisionColor } from './DetailComponents/DecisionUtils'
import QRCodeDialog from '../core/QRCodeDialog'
import QrCodeIcon from '@mui/icons-material/QrCode'
import CheckCircle from '@mui/icons-material/CheckCircle'
import CloseIcon from '@mui/icons-material/Close'
import { UpdateHistory } from '../EmployeeToolsMenu/ScaffoldUpdate/UpdateHistory'
import { ScaffoldUpdateRecord } from '../../types/Scaffold/ScaffoldRequest'

interface RowProps {
	scaffold: ScaffoldRequestForm
	projectTimeZone: string | null
	setAssignDialogScaffold: (scaffold: ScaffoldRequestForm) => void
	assigneeId?: number
	refreshScaffolds: () => Promise<void>
	createWorkItems: (
		workItemData: ScaffoldWorkItem[],
		metadataValues: Record<number, string>,
		scaffoldRequestId: number,
	) => Promise<void>
	setViewQRCode: (scaffold: ScaffoldRequestForm) => void
	setUpdateHistoryTagNumber: (tagNumber: number | null) => void
}

function Row({
	scaffold,
	projectTimeZone,
	setAssignDialogScaffold,
	assigneeId,
	refreshScaffolds,
	createWorkItems,
	setViewQRCode,
	setUpdateHistoryTagNumber,
}: RowProps) {
	const navigate = useNavigate()
	const location = useLocation()
	const { scaffoldRequestId: fromNotificationScaffoldRequestId, key } =
		(location.state as {
			scaffoldRequestId: string
			fromNotification: boolean
			searchWi: string
			key: number
		}) || {}
	const [open, setOpen] = useState(
		fromNotificationScaffoldRequestId
			? parseInt(fromNotificationScaffoldRequestId) === scaffold.id
			: false,
	)

	// Update open state when key changes
	useEffect(() => {
		if (fromNotificationScaffoldRequestId) {
			setOpen(parseInt(fromNotificationScaffoldRequestId) === scaffold.id)
		}
	}, [fromNotificationScaffoldRequestId, key, scaffold.id])

	return (
		<>
			<TableRow>
				<StyledTableCell>
					<IconButton
						aria-label='expand row'
						size='small'
						onClick={(e) => {
							e.stopPropagation()
							setOpen(!open)
						}}
					>
						{open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
					</IconButton>
				</StyledTableCell>
				<StyledTableCell>{scaffold.id}</StyledTableCell>
				<StyledTableCell>
					<Box
						onClick={() => {
							if (scaffold.tagNumber !== null) {
								setUpdateHistoryTagNumber(scaffold.tagNumber)
							}
						}}
						sx={(theme: Theme) => ({
							display: 'flex',
							alignItems: 'center',
							gap: 1,
							cursor: scaffold.tagNumber !== null ? 'pointer' : 'default',
							'&:hover':
								scaffold.tagNumber !== null
									? {
											backgroundColor: theme.palette.action.hover,
											borderRadius: 1,
										}
									: {},
							px: scaffold.tagNumber !== null ? 1 : 0,
							py: scaffold.tagNumber !== null ? 0.5 : 0,
						})}
					>
						{scaffold.tagNumber}
						{scaffold.lastUpdate != null && (
							<Box
								sx={(theme) => ({
									width: '24px',
									height: '24px',
									borderRadius: '50%',
									backgroundColor:
										theme.palette[
											scaffold.lastUpdate === 0
												? 'success'
												: scaffold.lastUpdate === 1
												? 'warning'
												: 'error'
										].main,
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center',
									border: `2px solid ${theme.palette.background.paper}`,
									zIndex: 1,
									flexShrink: 0,
								})}
							>
								<CheckCircle
									sx={{
										fontSize: '16px',
										color: 'white',
									}}
								/>
							</Box>
						)}
					</Box>
				</StyledTableCell>
				<StyledTableCell>
					{formatLocaleShortDateTime(scaffold.requestedAt, projectTimeZone ?? 'UTC')}
				</StyledTableCell>
				<StyledTableCell>{scaffold.action}</StyledTableCell>
				<StyledTableCell>
					<Chip
						label={scaffold.priority}
						size='small'
						variant='outlined'
						color={
							scaffold.priority === 'Urgent'
								? 'error'
								: scaffold.priority === 'High'
								? 'warning'
								: scaffold.priority === 'Medium'
								? 'info'
								: 'success'
						}
						sx={{
							fontWeight: 'medium',
							minWidth: '80px',
						}}
					/>
				</StyledTableCell>
				<StyledTableCell>
					<Typography
						sx={{
							border:
								scaffold.requiredDate &&
								new Date(scaffold.requiredDate).getTime() - new Date().getTime() <
									10 * 24 * 60 * 60 * 1000
									? '1px solid #f44336'
									: 'none',
							borderRadius:
								scaffold.requiredDate &&
								new Date(scaffold.requiredDate).getTime() - new Date().getTime() <
									10 * 24 * 60 * 60 * 1000
									? '6px'
									: '0',
							padding:
								scaffold.requiredDate &&
								new Date(scaffold.requiredDate).getTime() - new Date().getTime() <
									10 * 24 * 60 * 60 * 1000
									? '4px 8px'
									: '0',
							display: 'inline-block',
							backgroundColor:
								scaffold.requiredDate &&
								new Date(scaffold.requiredDate).getTime() - new Date().getTime() <
									10 * 24 * 60 * 60 * 1000
									? 'rgba(244, 67, 54, 0.1)'
									: 'transparent',
						}}
					>
						{formatLocaleShortDate(scaffold.requiredDate)}
					</Typography>
				</StyledTableCell>
				<StyledTableCell>{scaffold.contractorName}</StyledTableCell>
				<StyledTableCell align='center'>
					{scaffold.percentComplete ? formatPercent(scaffold.percentComplete) : ''}
				</StyledTableCell>
				<StyledTableCell align='center' style={{ whiteSpace: 'pre-line' }}>
					{scaffold.workItems && scaffold.workItems.length > 0 && (
						<IconButton
							onClick={(e) => {
								e.preventDefault()
								e.stopPropagation()
								navigate(`/workitems/overview`, {
									state: { fromNotification: true, workItemId: scaffold.workItems?.[0].id },
								})
							}}
						>
							<OpenInNewIcon sx={{ color: '#007BFF', fontSize: '1.4rem' }} />
						</IconButton>
					)}
				</StyledTableCell>
				<StyledTableCell>
					{!scaffold.estimation
						? 'New Request'
						: !scaffold.workItems
						? 'Estimated'
						: 'Work Items Created'}
				</StyledTableCell>
				<StyledTableCell align='center'>
					{scaffold.approvalDecision && (
						<Chip
							icon={getDecisionIcon(scaffold.approvalDecision) || undefined}
							label={scaffold.approvalDecision}
							color={getDecisionColor(scaffold.approvalDecision)}
							variant='filled'
							size='small'
							sx={{ fontWeight: 600 }}
						/>
					)}
				</StyledTableCell>
				<StyledTableCell align='center'>
					<IconButton
						size='medium'
						onClick={() => {
							setViewQRCode(scaffold)
						}}
						sx={{
							color: 'primary.main',
							opacity: 0.7,
							'&:hover': {
								opacity: 1,
							},
							p: 0,
						}}
					>
						<QrCodeIcon fontSize='medium' />
					</IconButton>
				</StyledTableCell>
			</TableRow>
			<TableRow>
				<StyledTableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={13} align='center'>
					<Collapse in={open} timeout='auto' unmountOnExit>
						<Box
							sx={{
								backgroundColor: '#e8f4fd',
								borderRadius: 2,
								m: 1,
								border: '2px solid #1976d2',
								boxShadow: '0 2px 6px rgba(25, 118, 210, 0.2)',
							}}
						>
							<RequestDetails
								scaffoldRequest={scaffold}
								setAssignDialogScaffold={setAssignDialogScaffold}
								assigneeId={assigneeId}
								refreshScaffolds={refreshScaffolds}
								createWorkItems={createWorkItems}
							/>
						</Box>
					</Collapse>
				</StyledTableCell>
			</TableRow>
		</>
	)
}

type ScaffoldOrderListProps = {
	filters: ScaffoldFilters
	setFilters: Dispatch<SetStateAction<ScaffoldFilters>>
	assigneeId?: number
}

export default function ScaffoldOrderList({
	filters,
	setFilters,
	assigneeId,
}: ScaffoldOrderListProps) {
	const [totalCount, setTotalCount] = useState(0)
	const [scaffolds, setScaffolds] = useState<ScaffoldRequestForm[]>([])
	const [isLoading, setIsLoading] = useState(false)
	const { projectTimeZone } = useProjectFeaturesContext()
	const [assignDialogScaffold, setAssignDialogScaffold] = useState<ScaffoldRequestForm | null>(null)
	const [viewQRCode, setViewQRCode] = useState<ScaffoldRequestForm | null>(null)
	const [updateHistoryTagNumber, setUpdateHistoryTagNumber] = useState<number | null>(null)
	const [updateRecords, setUpdateRecords] = useState<ScaffoldUpdateRecord[]>([])
	const [updateRecordsLoading, setUpdateRecordsLoading] = useState(false)
	const fetchScaffolds = async () => {
		const data = await getScaffoldOrders(filters, assigneeId)
		setScaffolds(data.scaffoldRequests)
		setTotalCount(data.totalCount)
	}

	useEffect(() => {
		setIsLoading(true)
		fetchScaffolds().finally(() => {
			setIsLoading(false)
		})
	}, [filters, assigneeId])

	async function createWorkItems(
		workItemData: ScaffoldWorkItem[],
		metadataValues: Record<number, string>,
		scaffoldRequestId: number,
	): Promise<void> {
		await createScaffoldWorkItem(workItemData, metadataValues, scaffoldRequestId)
	}

	useEffect(() => {
		if (!updateHistoryTagNumber) {
			return
		}
		const loadUpdateRecords = async () => {
			setUpdateRecordsLoading(true)
			try {
				const records = await getScaffoldUpdateRecord(updateHistoryTagNumber)
				setUpdateRecords(records)
			} catch (error) {
				console.error('Error getting update records:', error)
				setUpdateRecords([])
			} finally {
				setUpdateRecordsLoading(false)
			}
		}
		loadUpdateRecords()
	}, [updateHistoryTagNumber])

	const handleCloseUpdateHistoryDialog = () => {
		setUpdateHistoryTagNumber(null)
	}

	return (
		<>
			<Paper elevation={2} sx={{ height: '100%', width: '100%' }}>
				<TableContainer sx={{ height: '100%', width: '100%' }}>
					<Table stickyHeader>
						<TableHead>
							<TableRow>
								<StyledTableCell />
								<StyledTableCell>Scaffold Request ID</StyledTableCell>
								<StyledTableCell>Tag Number</StyledTableCell>
								<StyledTableCell>Requested At</StyledTableCell>
								<StyledTableCell>Action</StyledTableCell>
								<StyledTableCell>Priority</StyledTableCell>
								<StyledTableCell>Required Date</StyledTableCell>
								<StyledTableCell>Contractor Name</StyledTableCell>
								<StyledTableCell align='center'>Percent Complete</StyledTableCell>
								<StyledTableCell align='center'>Linked Work Items</StyledTableCell>
								<StyledTableCell>Status</StyledTableCell>
								<StyledTableCell align='center'>Approval Decision</StyledTableCell>
								<StyledTableCell align='center'>QR</StyledTableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{isLoading ? (
								<TableRow>
									<StyledTableCell colSpan={13} align='center'>
										<CircularProgress />
									</StyledTableCell>
								</TableRow>
							) : scaffolds.length > 0 ? (
								scaffolds.map((scaffold) => (
									<Row
										key={scaffold.id}
										scaffold={scaffold}
										projectTimeZone={projectTimeZone ?? null}
										setAssignDialogScaffold={setAssignDialogScaffold}
										assigneeId={assigneeId}
										refreshScaffolds={fetchScaffolds}
										createWorkItems={createWorkItems}
										setViewQRCode={setViewQRCode}
										setUpdateHistoryTagNumber={setUpdateHistoryTagNumber}
									/>
								))
							) : (
								<TableRow>
									<StyledTableCell colSpan={13} align='center'>
										No scaffolds orders found
									</StyledTableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</TableContainer>
			</Paper>
			<Box
				sx={{
					position: 'sticky',
					bottom: 0,
					py: 1,
					zIndex: 10,
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
				}}
			>
				<Pagination
					count={Math.ceil(totalCount / 10)}
					variant='outlined'
					shape='rounded'
					size='large'
					onChange={(_, page) => setFilters({ ...filters, page: page - 1 })}
					page={filters.page + 1}
				/>
			</Box>

			<AssignToDialog
				open={assignDialogScaffold !== null}
				onClose={() => setAssignDialogScaffold(null)}
				scaffoldRequest={assignDialogScaffold!}
				refreshScaffolds={fetchScaffolds}
			/>
			{viewQRCode && (
				<QRCodeDialog
					open={viewQRCode !== null}
					onClose={() => setViewQRCode(null)}
					primaryText={`Tag #${viewQRCode.tagNumber?.toString() ?? ''}`}
					secondaryText={''}
					qrCodeValue={viewQRCode.tagNumber?.toString() ?? ''}
					title='Scaffold QR Code'
				/>
			)}
			<Dialog
				open={updateHistoryTagNumber !== null}
				onClose={handleCloseUpdateHistoryDialog}
				fullWidth
				maxWidth='md'
			>
				<DialogTitle>
					Tag #{updateHistoryTagNumber}
					<IconButton
						onClick={handleCloseUpdateHistoryDialog}
						sx={(theme) => ({
							position: 'absolute',
							right: 8,
							top: 8,
							color: theme.palette.grey[500],
						})}
					>
						<CloseIcon />
					</IconButton>
				</DialogTitle>
				<DialogContent>
					{updateRecordsLoading ? (
						<Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
							<CircularProgress />
						</Box>
					) : (
						<UpdateHistory records={updateRecords} projectTimeZone={projectTimeZone ?? 'UTC'} />
					)}
				</DialogContent>
			</Dialog>
		</>
	)
}
